import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  signal,
  inject,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FormControl } from '@angular/forms';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideSearch, lucideTrash2 } from '@ng-icons/lucide';
import {
  ColumnDef,
  RowSelectionState,
} from '@3ddv/software-division-components/backoffice/table';
import { ButtonComponent } from '@3ddv/software-division-components/generic/button';
import { InputComponent } from '@3ddv/software-division-components/backoffice/input';
import { CardComponent } from '@3ddv/software-division-components/backoffice/card';
import { InfoGridComponent } from '@3ddv/software-division-components/backoffice/info-grid';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subscription } from 'rxjs';
import { GsTableComponent } from '../../shared/ui/table/gs-table.component';
import {
  AppDialogService,
  DialogCloseReason,
} from '../layout/dialog/app-dialog.service';
import { ApiResponse } from '../models/api.model';
import { DeleteAllComponent } from './modals/delete-all/delete-all.component';
import { DeleteComponent } from './modals/delete/delete.component';
import { RedisRecord } from './models/redis-manager.model';
import { buildRedisColumns } from './redis-manager.columns';
import {
  RedisManagerService,
  type RedisGetAllResult,
} from './services/redis-manager.service';

@Component({
  selector: 'app-redis-manager',
  templateUrl: './redis-manager.component.html',
  styleUrl: './redis-manager.component.css',
  imports: [
    ButtonComponent,
    InputComponent,
    CardComponent,
    InfoGridComponent,
    GsTableComponent,
    NgIcon,
  ],
  viewProviders: [provideIcons({ lucideSearch, lucideTrash2 })],
})
export class RedisManagerComponent implements OnInit, OnDestroy {
  private activatedRoute = inject(ActivatedRoute);
  private dialogService = inject(AppDialogService);
  private redisManagerService = inject(RedisManagerService);
  private toastrService = inject(ToastrService);
  private host = inject<ElementRef<HTMLElement>>(ElementRef, {
    optional: true,
  });

  public data = signal<RedisRecord[]>([]);
  public searchControl = new FormControl('', { nonNullable: true });
  public redisRecord: RedisRecord = null;
  public selectedKeys = signal(new Set<string>());
  public columns: ColumnDef<RedisRecord>[] = [];
  public tableEpoch = signal(0);
  public subscriptions: Array<Subscription>;
  public refreshing: boolean;
  public nextCursor: string;
  private submittedSearch = '';

  constructor() {
    this.data.set([]);
    this.selectedKeys.set(new Set());
    this.subscriptions = [];
    this.refreshing = false;
  }

  get json() {
    if (!this.redisRecord) {
      return '';
    }
    let json = null;

    try {
      json = JSON.parse(this.redisRecord?.value);
    } catch (error) {
      json = this.redisRecord?.value;
    }

    return JSON.stringify(json, undefined, 2);
  }

  ngOnInit(): void {
    this.columns = buildRedisColumns(
      record => this.selectRedisRecord(record),
      key => this.openModalDelete(key)
    );

    const resolved: ApiResponse<RedisGetAllResult> =
      this.activatedRoute.snapshot.data['api'];
    this.data.set(resolved.data.data);
    this.nextCursor = resolved.data.nextCursor;
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(o => o.unsubscribe());
  }

  public onSelectionChange(state: RowSelectionState): void {
    const keys = new Set<string>();
    Object.keys(state).forEach(index => {
      if (state[index]) {
        const record = this.data()[Number(index)];
        if (record) {
          keys.add(record.key);
        }
      }
    });
    this.selectedKeys.set(keys);
  }

  public onSearchSubmit(event: Event): void {
    event.preventDefault();
    this.redisRecord = null;
    this.submittedSearch = this.searchControl.value;
    this.getAll();
  }

  getAll() {
    this.refreshing = true;
    const all$ = this.redisManagerService
      .getAll$({ search: this.submittedSearch })
      .subscribe({
        next: res => {
          this.refreshing = false;
          this.data.set(res.data.data);
          this.nextCursor = res.data.nextCursor;
          this.selectedKeys.set(new Set());
          this.tableEpoch.update(epoch => epoch + 1);
        },
        error: () => {
          this.refreshing = false;
          this.toastrService.error(
            'An error occurred trying to get the redis keys',
            'Error'
          );
        },
      });
    this.subscriptions.push(all$);
  }

  selectRedisRecord(redisRecord: RedisRecord) {
    if (this.redisRecord?.key === redisRecord.key) {
      this.redisRecord = null;
    } else {
      this.redisRecord = redisRecord;
    }
  }

  public getNextKeys(event?: Event) {
    this.releaseFocusAfterPointerClick(event);

    if (!this.nextCursor || String(this.nextCursor) === '0') {
      return;
    }

    this.refreshing = true;
    const all$ = this.redisManagerService
      .getAll$({ cursor: this.nextCursor, search: this.submittedSearch })
      .subscribe({
        next: res => {
          this.refreshing = false;
          const newKeys = [...this.data(), ...res.data.data];
          this.data.set(newKeys);
          this.nextCursor = res.data.nextCursor;
        },
        error: () => {
          this.refreshing = false;
          this.toastrService.error(
            'An error occurred trying to get the redis keys',
            'Error'
          );
        },
      });
    this.subscriptions.push(all$);
  }

  public openModalDelete(key: string) {
    const { closed$ } = this.dialogService.open(DeleteComponent, {
      size: 'lg',
      inputs: { key },
    });
    this.removeKeysOnSuccess(closed$, [key]);
  }

  public openModalDeleteAll() {
    const keys = Array.from(this.selectedKeys());
    const { closed$ } = this.dialogService.open(DeleteAllComponent, {
      size: 'lg',
      inputs: { keys },
    });
    this.removeKeysOnSuccess(closed$, keys);
  }

  private removeKeysOnSuccess(
    closed$: Observable<DialogCloseReason | undefined>,
    keys: string[]
  ) {
    const onClose$ = closed$.subscribe(reason => {
      if (reason === 'success') {
        this.removeKeys(keys);
      }
    });
    this.subscriptions.push(onClose$);
  }

  private removeKeys(keys: string[]) {
    const removed = new Set(keys);
    const scrollTop = this.tableContainer()?.scrollTop ?? 0;

    this.data.set(this.data().filter(record => !removed.has(record.key)));
    this.selectedKeys.set(new Set());
    this.tableEpoch.update(epoch => epoch + 1);

    if (this.redisRecord && removed.has(this.redisRecord.key)) {
      this.redisRecord = null;
    }

    this.restoreScroll(scrollTop);
  }

  private releaseFocusAfterPointerClick(event?: Event) {
    const isPointerClick = event instanceof MouseEvent && event.detail > 0;
    const focused = document.activeElement;

    if (isPointerClick && focused instanceof HTMLElement) {
      focused.blur();
    }
  }

  private restoreScroll(scrollTop: number, framesLeft = 10) {
    if (!this.host || scrollTop === 0) {
      return;
    }

    requestAnimationFrame(() => {
      const container = this.tableContainer();

      if (container && container.scrollHeight > container.clientHeight) {
        container.scrollTop = scrollTop;
      } else if (framesLeft > 0) {
        this.restoreScroll(scrollTop, framesLeft - 1);
      }
    });
  }

  private tableContainer(): HTMLElement | null {
    return (
      this.host?.nativeElement.querySelector('.keys-table .table-container') ??
      null
    );
  }
}
